/** The Feedback list's table, its empty states and pagination. */

import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, MessagesSquare, Plus } from 'lucide-react';

import type { FeedbackRow as Item } from '@/api/types';
import { Pagination } from '@/ui/Pagination';
import { SortHeader } from '@/ui/SortHeader';
import { formatDate, formatDateTime } from '@/ui/format';

import type { FeedbackLinkState } from './feedbackParams';
import { CategoryLabel, StatusPill, relativeTime } from './feedbackUi';
import type { FeedbackPageState } from './useFeedbackPage';

function EmptyMessage({ state }: { state: FeedbackPageState }) {
  if (state.filtered) {
    return (
      <>
        <h3>No requests match</h3>
        <p>Clear the search or the filters to see the rest.</p>
      </>
    );
  }
  if (state.all) {
    return (
      <>
        <h3>No requests yet</h3>
        <p>What people report shows here for triage.</p>
      </>
    );
  }
  return (
    <>
      <h3>You have not filed a request yet</h3>
      <p>Found a bug, or have an idea to make Wynxx Discover better? Tell us.</p>
      {state.canSubmit && (
        <div className="empty-actions">
          <button type="button" className="btn btn-primary" onClick={() => state.setAdding(true)}>
            <Plus aria-hidden="true" /> New request
          </button>
        </div>
      )}
    </>
  );
}

function FeedbackRow({ item, all, from }: { item: Item; all: boolean; from: string }) {
  return (
    <tr>
      <td className="feedback-title-cell">
        <Link to={`/feedback/${item.id}`} state={{ from } satisfies FeedbackLinkState}>
          {item.title}
        </Link>
      </td>
      <td><CategoryLabel category={item.category} /></td>
      <td><StatusPill status={item.status} /></td>
      {all && <td>{item.author.display_name}</td>}
      <td className="num">{item.comment_count > 0 ? item.comment_count : <span className="hint">0</span>}</td>
      <td title={formatDateTime(item.last_activity_at) ?? undefined}>{relativeTime(item.last_activity_at)}</td>
      <td className="hint">{formatDate(item.created_at)}</td>
    </tr>
  );
}

function FeedbackRows({ state }: { state: FeedbackPageState }) {
  const location = useLocation();
  const rows = state.list.data?.items ?? [];
  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={state.all ? 7 : 6}>
          <div className="empty">
            <div className="e-icon"><MessagesSquare aria-hidden="true" /></div>
            <EmptyMessage state={state} />
          </div>
        </td>
      </tr>
    );
  }
  const from = `${location.pathname}${location.search}`;
  return (
    <>
      {rows.map((item) => (
        <FeedbackRow key={item.id} item={item} all={state.all} from={from} />
      ))}
    </>
  );
}

export function FeedbackTable({ state }: { state: FeedbackPageState }) {
  const { params, update, all } = state;
  const total = state.list.data?.total ?? 0;
  const header = (label: string, key: string) => (
    <SortHeader label={label} sortKey={key} sort={params.sort} order={params.order} onSort={state.onSort} />
  );
  return (
    <>
      <div className="table-scroll">
        <table className="data zebra compact feedback-table">
          <thead>
            <tr>
              {header('Request', 'title')}
              {header('Type', 'category')}
              {header('Status', 'status')}
              {all && header('Filed by', 'author')}
              <th scope="col" className="num">
                <MessageSquare aria-hidden="true" className="th-icon" />
                <span className="sr-only">Comments</span>
              </th>
              {header('Last activity', 'last_activity_at')}
              {header('Filed', 'created_at')}
            </tr>
          </thead>
          <tbody>
            <FeedbackRows state={state} />
          </tbody>
        </table>
      </div>
      {total > 0 && (
        <Pagination
          page={params.page}
          pageSize={params.page_size}
          total={total}
          noun="requests"
          onPage={(page) => update({ page })}
          onPageSize={(page_size) => update({ page_size })}
        />
      )}
    </>
  );
}
